import React from 'react';
import { useTranslation } from 'react-i18next';
import { Product, ProductType, getProductImageUrl } from '../../../../models/product/product';
import { Book, BookFormat } from '../../../../models/product/book';
import { currencyService } from '../../../../services/currency-service';

interface ProductCardProps {
  product: Product;
  className?: string;
  // CTA Configuration
  showAddToCart?: boolean;
  showQuickView?: boolean;
  onAddToCart?: (format?: BookFormat) => void;
  onViewDetails?: () => void;
  isAddingToCart?: boolean;
  isRTL?: boolean;
}

const ProductCard: React.FC<ProductCardProps> = ({ 
  product,
  className = '',
  showAddToCart = true,
  showQuickView = true,
  onAddToCart,
  onViewDetails,
  isAddingToCart = false,
  isRTL = false
}) => {
  const { t } = useTranslation();
  const [imageLoading, setImageLoading] = React.useState(true);
  const [imageError, setImageError] = React.useState(false);

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
  };

  const handleAddToCart = () => {
    if (onAddToCart) {
      if (product.type === ProductType.BOOK) {
        const book = product as Book;
        // Default to physical format, user can change in modal
        onAddToCart(book.bookFormat === BookFormat.BOTH ? BookFormat.PHYSICAL : book.bookFormat);
      } else {
        onAddToCart();
      }
    }
  };

  const handleViewDetails = () => {
    if (onViewDetails) {
      onViewDetails();
    } else {
      // Default navigation behavior
      window.location.href = `/store/product/${product.id}`;
    }
  };

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow group flex flex-col h-full ${className}`}>
      {/* Product Cover */}
      <div className="relative aspect-[3/4] overflow-hidden bg-gray-100 flex items-center justify-center">
        {/* Loading Placeholder */}
        {imageLoading && (
          <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
            <div className="flex flex-col items-center space-y-3">
              <svg className="w-8 h-8 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <div className="text-xs text-gray-500">{t('ui.loading', 'Loading...')}</div>
            </div>
          </div>
        )}

        {/* Error Placeholder */}
        {imageError && (
          <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
            <div className="flex flex-col items-center space-y-2 text-gray-400">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <div className="text-xs">Image unavailable</div>
            </div>
          </div>
        )}

        <img
          src={getProductImageUrl(product)}
          alt={product.name}
          className={`book-cover w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300 ${imageLoading ? 'opacity-0' : 'opacity-100'}`}
          onLoad={handleImageLoad}
          onError={handleImageError}
        />
        
        {/* Quick Actions Overlay */}
        {showQuickView && (
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
            <button
              onClick={handleViewDetails}
              className="bg-white text-black px-4 py-2 rounded-lg font-medium hover:bg-gray-100 transition-colors transform scale-90 group-hover:scale-100"
            >
              {t('general.actions.viewDetails', 'Quick View')}
            </button>
          </div>
        )}

        {/* Stock Status */}
        {!product.inStock && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <span className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-medium">
              {t('products.status.out-of-stock', 'Out of Stock')}
            </span>
          </div>
        )}
      </div>

      {/* Product Info */}
      <div className="p-4 flex flex-col h-full">
        <div className="flex-grow">
          {/* Title */}
          <h3 className="font-semibold text-gray-900 mb-1 line-clamp-2 group-hover:text-black transition-colors">
            {product.name}
          </h3>

          {/* Author for books */}
          {product.type === ProductType.BOOK && (
            <p className="text-sm text-gray-600 mb-2">
              {t('products.books.by-author', 'by')} <span className="font-medium">{(product as Book).author}</span>
            </p>
          )}

          {/* Category */}
          <p className="text-xs text-gray-500 mb-3">
            {/* {t(`products.books.categories.${product.category}`, product.category)} */}
          </p>

          {/* Description */}
          <p className="text-gray-600 text-sm line-clamp-3 mb-3">
            {product.description}
          </p>

          {/* Price */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <span className="text-lg font-bold text-black">
                {currencyService.formatPrice(product.price)}
              </span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className={`flex gap-2 mt-4 ${isRTL ? 'space-x-reverse' : ''}`}>
          {/* Add to Cart Button - only show if configured */}
          {showAddToCart && (
            <button
              onClick={handleAddToCart}
              disabled={!product.inStock || isAddingToCart}
              className="flex-1 bg-black text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {isAddingToCart ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>{t('cart.actions.adding-to-cart', 'Adding...')}</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m1.6 8L5 3H3m4 10v6a1 1 0 001 1h10a1 1 0 001-1v-6M9 19a1 1 0 102 0 1 1 0 00-2 0zm8 0a1 1 0 102 0 1 1 0 00-2 0z" />
                  </svg>
                  <span>
                    {product.inStock 
                      ? t('cart.actions.add-to-cart', 'Add to Cart')
                      : t('products.status.out-of-stock', 'Out of Stock')
                    }
                  </span>
                </>
              )}
            </button>
          )}
          
          {/* View Details Button - show if no add to cart, or as secondary button */}
          {(!showAddToCart || showQuickView) && (
            <button
              onClick={handleViewDetails}
              className={`${!showAddToCart ? 'flex-1 bg-black text-white' : ''} p-2 border border-gray-300 rounded-lg hover:border-black transition-colors ${!showAddToCart ? 'font-medium' : ''}`}
              title={t('general.actions.view-details', 'View Details')}
            >
              {!showAddToCart ? (
                <span>{t('general.actions.view-details', 'View Details')}</span>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
